import { DateValueI } from '@/components/ui'
import { SavedFilterI } from '@/features/incidents-table'
import {
  ActiveIncidentsFilterQuery,
  IncidentFiltersEnrichmentQuery,
} from '@/graphql/generated/operations'
import {
  AiOperatorIncidentState,
  DeviceType,
  IncidentFilterCriteria,
  IncidentFilterInput,
  IncidentPriority,
  IncidentSource,
  IncidentStatus,
  IncidentType,
  MutedStatus,
} from '@/graphql/generated/schemas'
import {
  aiOperatorStateOptions,
  incidentTypeOptions as automatedTypeOptions,
  clipsOptions,
  createdAtIntervalOptions,
  incidentStatusOptions,
  mutedOptions,
  priorityOptions,
  sourceTypeOptions,
} from '@/redux/incidents/incidentsFilterOptions'
import { ExtraIncidentFilterCriteria } from '@/redux/incidents/incidentsSlice'
import { FilterOptionI, isCustomDateRange } from '@/utils/filterDrawers'
import { getIncidentCategoryOptions } from '@/utils/forms/optionBuilders'

import { IncidentFiltersFormInputsI } from '../../features/incidents-table/incidents-filter-drawer/types/form'
import {
  combineIncidentType,
  combineSourceType,
  filterByType,
  getCreatedAtGteTimestamp,
  getCustomDateFilter,
  getFilterFromLabels,
  getFilterFromOptions,
  getLabels,
  getSingleFilterFromOptions,
  parseDisplayIdQuery,
  removeNullValues,
  removeTypename,
} from './utils'

/**
 * These functions implement conversions between incident filter form state and filter criteria.
 */

const now = Date.now()

export const getMutedFilterCriteria = (
  filter: FilterOptionI<MutedStatus>
): IncidentFilterCriteria =>
  filter ? { mutedStatus: filter.value } : { mutedStatus: MutedStatus.All }

export const getClipsFilterCriteria = (filter: FilterOptionI<boolean>) =>
  filter ? { hasVideoClip: filter.value } : {}

export const getAiOperatorStateInFilterCriteria = (
  filter: FilterOptionI<AiOperatorIncidentState[]>
): IncidentFilterCriteria =>
  filter?.value?.length ? { aiOperatorStateIn: filter.value } : {}

export const getFacilityIdInFilterCriteria = (
  filter: FilterOptionI[]
): IncidentFilterCriteria =>
  filter?.length ? { facilityIdIn: filter?.map((f) => f.value) } : {}

export const getFloorIdInFilterCriteria = (
  filter: FilterOptionI[]
): IncidentFilterCriteria =>
  filter?.length ? { floorIdIn: filter?.map((f) => f.value) } : {}

export const getCreatedAtGteIntervalFilterCriteria = (
  filter: FilterOptionI<number>
): IncidentFilterCriteria =>
  filter?.value ? { createdAtGteInterval: filter.value } : {}

export const getCreatedAtGteANDLteFilterCriteria = (
  filter: FilterOptionI<DateValueI>
): IncidentFilterCriteria =>
  filter?.value
    ? { createdAtGte: filter.value.gte, createdAtLte: filter.value.lte }
    : {}

export const getStatusInFilterCriteria = (
  filter: FilterOptionI<IncidentStatus>[]
): IncidentFilterCriteria =>
  filter?.length ? { statusIn: filter?.map((f) => f.value) } : {}

export const getPriorityInFilterCriteria = (
  filter: FilterOptionI<IncidentPriority>[]
): IncidentFilterCriteria =>
  filter?.length ? { priorityIn: filter?.map((f) => f.value) } : {}

export const getDeviceIdInFilterCriteria = (
  filter: FilterOptionI[]
): IncidentFilterCriteria =>
  filter?.length ? { deviceIdIn: filter?.map((f) => f.value) } : {}

export const getIncidentSourceCombinedFilterCriteria = (
  filter: FilterOptionI<IncidentSource | DeviceType>[]
): IncidentFilterCriteria => {
  const sourcesArray = filterByType(filter, IncidentSource)
  const deviceTypesArray = filterByType(filter, DeviceType)
  const combinedFilters = [
    sourcesArray.length > 0 && { sourceIn: sourcesArray },
    deviceTypesArray.length > 0 && { deviceTypeIn: deviceTypesArray },
  ].filter(Boolean)

  return combinedFilters.length > 0
    ? ({ OR: combinedFilters } as unknown as IncidentFilterCriteria)
    : {}
}

export const getIncidentTypeCombinedFilterCriteria = (
  filter: FilterOptionI<IncidentType | string>[]
): IncidentFilterCriteria => {
  const incidentTypesArray = filterByType(filter, IncidentType)

  /**
   * This array should contain all values from MultiFilter that are NOT corresponding to
   * IncidentType's. For example if the MultiFilter contains following values:
   *
   * [
   *  { value: "TAILGATING", label: "..." },
   *  { value: "DOOR_HELD_OPEN", label: "..." },
   *  { value: "f57edeb8-ba00-4206-bfd5-4c4869d59d0a", label: "..." },
   *  { value: "b17e235f-1864-4b44-86ae-3fd51f68adc2", label: "..." }
   * ]
   *
   * then the resulting incidentCategoryIdsArray would be:
   *
   * [ "f57edeb8-ba00-4206-bfd5-4c4869d59d0a", "b17e235f-1864-4b44-86ae-3fd51f68adc2" ]
   *
   *  */
  const incidentCategoryIdsArray =
    filter
      ?.map((f) => f.value)
      .filter(
        (v) => !incidentTypesArray.map((t) => t.toString()).includes(v)
      ) || []

  const combinedFilters = [
    incidentTypesArray.length > 0 && { typeIn: incidentTypesArray },
    incidentCategoryIdsArray.length > 0 && {
      manualIncidentCategoryIdIn: incidentCategoryIdsArray,
    },
  ].filter(Boolean)

  return combinedFilters.length > 0
    ? ({ AND: [{ OR: combinedFilters }] } as unknown as IncidentFilterCriteria)
    : {}
}

const DISPLAY_ID_DELIMITER = ','

/**
 * Conversion between Redux state and IncidentFilterInput.
 *
 * Converts `createdAtGteInterval` to `createdAtGte` timestamp. If `query` field
 * contains commas, parses it to `displayIdIn` array; otherwise keeps `query` as-is.
 */
export const buildFilterInput = (
  savedFilter: IncidentFilterCriteria,
  extraFilter?: ExtraIncidentFilterCriteria
): IncidentFilterInput => {
  const savedFilterInput: IncidentFilterInput = {
    ...savedFilter,
    ...(savedFilter?.createdAtGteInterval
      ? {
          createdAtGte: getCreatedAtGteTimestamp(
            now,
            savedFilter.createdAtGteInterval
          ),
        }
      : {}),
  }

  const queryFilter: Partial<IncidentFilterInput> = {}
  const hasMultipleDisplayIds =
    extraFilter?.query?.includes(DISPLAY_ID_DELIMITER)

  let finalExtraFilter = extraFilter || {}

  if (hasMultipleDisplayIds) {
    const displayIds = parseDisplayIdQuery(
      extraFilter.query,
      DISPLAY_ID_DELIMITER
    )
    if (displayIds.length > 0) {
      queryFilter.displayIdIn = displayIds
      // eslint-disable-next-line no-unused-vars
      const { query, ...restExtraFilter } = extraFilter
      finalExtraFilter = restExtraFilter
    }
  }

  return JSON.parse(
    JSON.stringify({
      ...removeNullValues(savedFilterInput || {}),
      ...removeNullValues(queryFilter || {}),
      ...removeNullValues(finalExtraFilter || {}),
    })
  )
}

/**
 * Conversion between incident filter form state and IncidentFilterCriteria.
 *
 * It is used for:
 *  - building `createSavedFilter` mutation input from form state
 *  - building `updateSavedFilter` mutation input from form state
 *  - applying filters in standard mode
 */
export const buildFilterCriteria = (
  formState: IncidentFiltersFormInputsI
): IncidentFilterCriteria => {
  return {
    ...getFacilityIdInFilterCriteria(formState.facilityName),
    ...getFloorIdInFilterCriteria(formState.floorName),
    ...getDateFilterCriteria(formState.createdAt),
    ...getStatusInFilterCriteria(formState.incidentStatus),
    ...getPriorityInFilterCriteria(formState.priority),
    ...getIncidentSourceCombinedFilterCriteria(formState.sourceType),
    ...getIncidentTypeCombinedFilterCriteria(formState.incidentType),
    ...getMutedFilterCriteria(formState.mutedStatus),
    ...getDeviceIdInFilterCriteria(formState.deviceName),
    ...getClipsFilterCriteria(formState.clips),
    ...getAiOperatorStateInFilterCriteria(formState.aiOperatorState),
  }
}

/**
 * Conversion between IncidentFilterCriteria and filter form state.
 *
 * It requires providing extra data to generate form options labels.
 *
 * It is used for:
 *  - building filter form state from `savedFilter` query response
 */
export const buildFormState = (
  criteria: IncidentFilterCriteria,
  enrichmentData: IncidentFiltersEnrichmentQuery
): IncidentFiltersFormInputsI => {
  const criteriaFacilityIds = criteria?.facilityIdIn || []
  const criteriaFloorIds = criteria?.floorIdIn || []
  const criteriaDeviceIds = criteria?.deviceIdIn || []
  const criteriaCreatedAt = criteria?.createdAtGteInterval
  const criteriaSourceType = combineSourceType(criteria?.OR)
  const criteriaIncidentType = combineIncidentType(criteria?.AND?.[0]?.OR)
  const criteriaPriorityStatus = criteria?.priorityIn || []
  const criteriaIncidentStatus = criteria?.statusIn || []
  const criteriaCreatedAtGte = criteria?.createdAtGte
  const criteriaCreatedAtLte = criteria?.createdAtLte

  const createdAt = criteriaCreatedAt
    ? getSingleFilterFromOptions(criteriaCreatedAt, createdAtIntervalOptions)
    : criteriaCreatedAtGte && criteriaCreatedAtLte
    ? getCustomDateFilter(criteriaCreatedAtGte, criteriaCreatedAtLte)
    : null

  const incidentTypeOptions = [
    ...automatedTypeOptions,
    ...getIncidentCategoryOptions(enrichmentData),
  ]

  const { facilityLabels, floorLabels, deviceLabels } =
    getLabels(enrichmentData)

  return {
    facilityName: getFilterFromLabels(criteriaFacilityIds, facilityLabels),
    floorName: getFilterFromLabels(criteriaFloorIds, floorLabels),
    deviceName: getFilterFromLabels(criteriaDeviceIds, deviceLabels),
    createdAt,
    incidentStatus: getFilterFromOptions(
      criteriaIncidentStatus,
      incidentStatusOptions
    ),
    sourceType: getFilterFromOptions(criteriaSourceType, sourceTypeOptions),
    priority: getFilterFromOptions(criteriaPriorityStatus, priorityOptions),
    incidentType: getFilterFromOptions(
      criteriaIncidentType,
      incidentTypeOptions
    ),
    mutedStatus: getSingleFilterFromOptions(
      criteria?.mutedStatus,
      mutedOptions
    ),
    clips: getSingleFilterFromOptions(criteria?.hasVideoClip, clipsOptions),
    aiOperatorState: getSingleFilterFromOptions(
      criteria?.aiOperatorStateIn,
      aiOperatorStateOptions
    ),
  }
}

/**
 * Conversion between data returned from `activeFilter` query and IncidentFilterCriteria.
 *
 * Both objects have the same structure but return data contains __typename fields and null values
 * which should be removed because they cause errors in the `incidents` query.
 *
 * It is used for:
 *  - synchronizing DB state with active filter Redux state
 */
export const buildActiveFilterCriteria = (
  data: ActiveIncidentsFilterQuery
): IncidentFilterCriteria => {
  const criteria = data?.activeFilter?.filterCriteria?.incident || {}
  const sourceCriteria = criteria?.OR
  const typeCriteria = criteria?.AND?.[0]?.OR

  const filter = removeNullValues({
    ...removeTypename(criteria),
    ...(sourceCriteria
      ? { OR: sourceCriteria.map((c) => removeTypename(c)) }
      : {}),
    ...(typeCriteria
      ? { AND: [{ OR: typeCriteria.map((c) => removeTypename(c)) }] }
      : {}),
  })

  return filter
}

export const buildActiveFilter = (
  data: ActiveIncidentsFilterQuery
): SavedFilterI => {
  const id = data?.activeFilter?.id
  const name = data?.activeFilter?.name
  if (id !== undefined && name !== undefined) {
    return { id, name }
  }
  return null
}

/**
 * Extends the given `IncidentFilterInput` by ensuring that the `mutedStatus` filter is propagated
 * to all AND and OR filter expressions. This is necessary because the backend treats
 * a missing `mutedStatus` value as `NOT_MUTED`, which can lead to unintended filtering behavior.
 *
 * Without this propagation, filters using AND and OR expressions may implicitly exclude
 * muted incidents, causing incorrect results.
 */
export const extendMutedFilter = (
  filters: IncidentFilterInput,
  shouldOnlyShowAlarmed: boolean
): IncidentFilterInput => {
  // If shouldOnlyShowAlarmed is true, mutedStatus should be overwritten
  const mutedStatus = shouldOnlyShowAlarmed
    ? MutedStatus.NotMuted
    : filters.mutedStatus

  const orFilter = filters?.OR?.[0]
  const andFilter = filters?.AND?.[0]
  const andOrFilters = andFilter?.OR

  const hasOr = orFilter !== undefined
  const hasAnd = andFilter !== undefined
  const hasMuted = mutedStatus !== undefined

  if (!hasMuted) {
    return filters
  }

  const extendedOr = hasOr ? [{ ...orFilter, mutedStatus }] : filters?.OR
  const extendedAnd = hasAnd
    ? [
        {
          OR: andOrFilters?.map((filter) => ({ ...filter, mutedStatus })),
          mutedStatus,
        },
      ]
    : filters?.AND

  return { ...filters, mutedStatus, AND: extendedAnd, OR: extendedOr }
}

/**
 * Checks to which date filter to is selected.
 *
 * If the 'Custom' option is selected we use the gte, lte filter
 * Otherwise we use the gteInterval filter
 *
 */
export const getDateFilterCriteria = (
  option: FilterOptionI<number | DateValueI>
): IncidentFilterCriteria => {
  return isCustomDateRange(option)
    ? getCreatedAtGteANDLteFilterCriteria(option as FilterOptionI<DateValueI>)
    : getCreatedAtGteIntervalFilterCriteria(option as FilterOptionI<number>)
}
